import React from 'react';
import { cn } from '../../lib/cn';

export type PageBandFigureTone = 'default' | 'amber' | 'salmon' | 'mint';

export interface PageBandFigure {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: PageBandFigureTone;
}

export interface PageBandProps {
  /** Mono uppercase kicker above the headline. */
  kicker?: React.ReactNode;
  /** Serif statement headline; may contain a single <em> (amber by default). */
  headline: React.ReactNode;
  /** Optional lead paragraph under the headline. */
  lead?: React.ReactNode;
  /** Right-aligned slot in the top row — usually a SegmentedControl or actions. */
  right?: React.ReactNode;
  /** Figures row (statement layout). Rendered divided by hairline rules. */
  figures?: PageBandFigure[];
  /** Replace the headline with skeleton lines. */
  loading?: boolean;
  className?: string;
}

const FIGURE_COLOR: Record<PageBandFigureTone, string> = {
  default: 'var(--color-foreground,#1c2426)',
  amber: 'var(--color-warning,#8a5a00)',
  salmon: 'var(--color-destructive,#b42318)',
  mint: 'var(--color-primary,#0e7c78)',
};

export function PageBand({
  kicker,
  headline,
  lead,
  right,
  figures,
  loading = false,
  className,
}: PageBandProps) {
  return (
    <header className={cn('bg-transparent', className)} style={{ padding: '32px 36px 30px' }}>
      <div className="flex flex-wrap items-end justify-between" style={{ gap: '16px 40px' }}>
        <div className="min-w-0">
          {kicker ? (
            <div className="font-[var(--font-ds-mono,var(--font-mono))] text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted-foreground,#647174)]">
              {kicker}
            </div>
          ) : null}
          {loading ? (
            <div className="mt-[10px] space-y-[10px]">
              <div className="h-[38px] w-[560px] max-w-full rounded-[8px] bg-[var(--color-muted,#edf2f1)]" />
              <div className="h-[38px] w-[360px] max-w-full rounded-[8px] bg-[var(--color-muted,#edf2f1)]" />
            </div>
          ) : (
            <h1
              className="font-[var(--font-serif,Georgia,serif)] font-medium text-[var(--color-foreground,#1c2426)] [&_em]:italic [&_em]:font-medium [&_em]:text-[var(--color-warning,#8a5a00)]"
              style={{
                fontSize: 38,
                lineHeight: 1.14,
                letterSpacing: '-0.01em',
                maxWidth: 820,
                marginTop: kicker ? 10 : 0,
              }}
            >
              {headline}
            </h1>
          )}
          {lead ? (
            <p
              className="mt-2 text-[15.5px] text-[var(--color-muted-foreground,#647174)]"
              style={{ maxWidth: 680 }}
            >
              {lead}
            </p>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>

      {figures?.length ? (
        <div
          className="mt-7 grid"
          style={{ gridTemplateColumns: `repeat(${String(figures.length)},minmax(0,1fr))` }}
        >
          {figures.map((figure, index) => (
            <div
              key={index}
              style={{
                padding: '0 22px',
                borderLeft: index === 0 ? 'none' : '1px solid var(--color-border,#e3e8e7)',
              }}
            >
              <div className="font-[var(--font-ds-mono,var(--font-mono))] text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted-foreground,#647174)]">
                {figure.label}
              </div>
              {loading ? (
                <div className="mt-[6px] h-[46px] w-[120px] rounded-[8px] bg-[var(--color-muted,#edf2f1)]" />
              ) : (
                <div
                  className="font-[var(--font-serif,Georgia,serif)] font-medium tabular-nums"
                  style={{
                    fontSize: 46,
                    lineHeight: 1.05,
                    marginTop: 6,
                    color: FIGURE_COLOR[figure.tone ?? 'default'],
                  }}
                >
                  {figure.value}
                </div>
              )}
              {figure.sub ? (
                <div className="mt-1 text-[13px] text-[var(--color-muted-foreground,#647174)]">
                  {figure.sub}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </header>
  );
}
